import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Typography,
  Button,
  Space,
  Tag,
  Row,
  Col,
  Card,
  Table,
  Descriptions,
  Empty,
  Spin,
  Tooltip,
  Popconfirm,
} from "antd";
import {
  ArrowLeftOutlined,
  ReloadOutlined,
  PoweroffOutlined,
  CameraOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  getDeviceHealth,
  resolveAlert,
  getUser,
  createTicketFromAlert,
} from "../network/api";
import {
  HEALTH_STATE_LABEL,
  HEALTH_STATE_TAG_COLOR,
  HEALTH_DOT_COLOR,
  HEALTH_STATE_COLOR,
  ALERT_SEVERITY_TAG_COLOR,
  ALERT_STATUS_TAG_COLOR,
  relativeTime,
  HARDWARE_ADMIN_RANK,
  pictureUrlFromPath,
} from "../constants/health";
import { rankOf } from "../constants/roles";
import { TICKET_TYPES } from "../constants/tickets";
import PageHeader from "./common/PageHeader";
import InfoHint from "./common/InfoHint";
import AddTicket from "./Modals/addTicket";
import { notify, serverError } from "../utils/notify";

const { Text } = Typography;

const C5_TOOLTIP = "Available in C5 (remote commands)";

const DeviceHealth = () => {
  const { deviceId } = useParams();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentRole, setCurrentRole] = useState(null);
  const [ticketModalOpen, setTicketModalOpen] = useState(false);

  const fetchHealth = useCallback(async () => {
    if (!deviceId) return;
    setLoading(true);
    try {
      const response = await getDeviceHealth(deviceId);
      setData(response);
    } catch (error) {
      console.error("Error fetching device health:", error);
      notify.error(serverError(error, "Failed to load device health."));
    } finally {
      setLoading(false);
    }
  }, [deviceId]);

  const fetchRole = useCallback(async () => {
    // Seed from cache, then refresh from the API (mirrors sider.js).
    const cached = localStorage.getItem("user");
    if (cached) {
      try {
        setCurrentRole(JSON.parse(cached)?.role || null);
      } catch (e) {
        // ignore malformed cache
      }
    }
    try {
      const user = await getUser();
      setCurrentRole(user?.role || null);
    } catch (error) {
      console.error("Error fetching user role:", error);
    }
  }, []);

  useEffect(() => {
    fetchHealth();
  }, [fetchHealth]);

  useEffect(() => {
    fetchRole();
  }, [fetchRole]);

  const handleResolve = async (alertId) => {
    try {
      await resolveAlert(alertId);
      notify.success("Alert resolved successfully!");
      fetchHealth();
    } catch (error) {
      console.error("Error resolving alert:", error);
      notify.error(serverError(error, "Failed to resolve alert."));
    }
  };

  const handleCreateTicket = async (alertId) => {
    try {
      const response = await createTicketFromAlert(alertId);
      const ticketId = response?.ticket?._id;
      notify.success("Ticket created from alert.");
      if (ticketId) navigate(`/dashboard/tickets/${ticketId}`);
    } catch (error) {
      console.error("Error creating ticket from alert:", error);
      // 409 -> an open ticket already exists; surface the server message.
      notify.error(serverError(error, "Failed to create ticket from alert."));
    }
  };

  const canResolve = rankOf(currentRole) >= HARDWARE_ADMIN_RANK;
  const canCreateTicket = rankOf(currentRole) >= HARDWARE_ADMIN_RANK;

  const health = data?.health || {};
  const state = health.state;
  const color = health.color || HEALTH_STATE_COLOR[state];
  const reasons = health.reasons || [];
  const recentStats = data?.recentStats || [];
  const alerts = data?.alerts || [];
  const effectiveRule = data?.effectiveRule || {};
  const lastPicture = data?.lastPicture || null;
  const pictureUrl = pictureUrlFromPath(lastPicture?.path);

  // recentStats is oldest -> newest (ready to chart left -> right).
  // Note the backend's misspelled `cpuTemprature` key.
  const chartData = recentStats.map((s) => ({
    time: new Date(s.createdAt).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    }),
    temperature: s.cpuTemprature,
    storage: s.storageUsed,
    cpu: s.cpuUsage,
  }));

  const alertColumns = [
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
    },
    {
      title: "Severity",
      dataIndex: "severity",
      key: "severity",
      render: (severity) => (
        <Tag color={ALERT_SEVERITY_TAG_COLOR[severity] || "default"}>
          {severity}
        </Tag>
      ),
    },
    {
      title: "Message",
      dataIndex: "message",
      key: "message",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag color={ALERT_STATUS_TAG_COLOR[status] || "default"}>{status}</Tag>
      ),
    },
    {
      title: "Opened",
      dataIndex: "openedAt",
      key: "openedAt",
      render: (openedAt) => relativeTime(openedAt),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => {
        if (record.status !== "open") return null;
        if (!canResolve && !canCreateTicket) return null;
        return (
          <Space size="small">
            {canCreateTicket && (
              <Button
                size="small"
                onClick={() => handleCreateTicket(record._id)}
              >
                Create Ticket
              </Button>
            )}
            {canResolve && (
              <Popconfirm
                title="Resolve this alert?"
                okText="Resolve"
                cancelText="Cancel"
                onConfirm={() => handleResolve(record._id)}
              >
                <Button size="small">Resolve</Button>
              </Popconfirm>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <Spin spinning={loading}>
      <div>
        <Space style={{ marginBottom: 12 }}>
          <Button
            type="link"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate("/dashboard/health")}
            style={{ paddingLeft: 0 }}
          >
            Back to Fleet Health
          </Button>
        </Space>

        {/* Header */}
        <PageHeader
          icon={
            <span
              style={{
                display: "inline-block",
                width: 14,
                height: 14,
                borderRadius: "50%",
                backgroundColor: HEALTH_DOT_COLOR[color] || "#d9d9d9",
              }}
            />
          }
          title={
            <Space align="center" size="middle" wrap>
              {data?.deviceTitle || deviceId}
              {state && (
                <Tag
                  color={HEALTH_STATE_TAG_COLOR[state]}
                  style={{ fontSize: 14, padding: "2px 10px" }}
                >
                  {HEALTH_STATE_LABEL[state] || state}
                </Tag>
              )}
            </Space>
          }
          subtitle={`Device ID: ${data?.deviceId || deviceId} · Since: ${
            health.since ? relativeTime(health.since) : "—"
          }`}
          extra={
            <>
              {/* C5 remote commands — not yet implemented. */}
              <Tooltip title={C5_TOOLTIP}>
                <Button icon={<PoweroffOutlined />} disabled>
                  Restart
                </Button>
              </Tooltip>
              <Tooltip title={C5_TOOLTIP}>
                <Button icon={<CameraOutlined />} disabled>
                  Snapshot
                </Button>
              </Tooltip>
              {canCreateTicket && (
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setTicketModalOpen(true)}
                >
                  Create Ticket
                </Button>
              )}
              <Button
                icon={<ReloadOutlined />}
                onClick={fetchHealth}
                loading={loading}
              >
                Refresh
              </Button>
            </>
          }
        />

        <AddTicket
          visible={ticketModalOpen}
          onClose={() => setTicketModalOpen(false)}
          onSubmit={fetchHealth}
          deviceId={data?.deviceId || deviceId}
          defaultType={TICKET_TYPES[state] || TICKET_TYPES.MAINTENANCE}
          titleHint={`${HEALTH_STATE_LABEL[state] || "Issue"} — ${data?.deviceId || deviceId}`}
        />

        {reasons.length > 0 && (
          <div style={{ marginTop: -4, marginBottom: 8 }}>
            <Space wrap>
              {reasons.map((r) => (
                <Tag key={r}>{r}</Tag>
              ))}
            </Space>
          </div>
        )}

        <Row gutter={16} style={{ marginTop: 16 }}>
          {/* Telemetry trend */}
          <Col xs={24} lg={16}>
            <Card title={<span>Telemetry Trend<InfoHint title="Recent device readings over time: CPU temperature (°C), storage used, and CPU usage. Rising temperature or storage can predict trouble." /></span>} style={{ height: "100%" }}>
              {chartData.length === 0 ? (
                <Empty description="No recent telemetry" />
              ) : (
                <div style={{ height: 320 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="time" />
                      <YAxis />
                      <RechartsTooltip />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="temperature"
                        name="Temperature (°C)"
                        stroke="#ff4d4f"
                        strokeWidth={2}
                        dot={false}
                      />
                      <Line
                        type="monotone"
                        dataKey="storage"
                        name="Storage Used"
                        stroke="#1890ff"
                        strokeWidth={2}
                        dot={false}
                      />
                      <Line
                        type="monotone"
                        dataKey="cpu"
                        name="CPU Usage"
                        stroke="#52c41a"
                        strokeWidth={2}
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
            </Card>
          </Col>

          {/* Last image */}
          <Col xs={24} lg={8}>
            <Card title={<span>Last Image<InfoHint title="The most recent photo this camera captured and uploaded. A green “QR” tag means a valid QR code was read in the frame." /></span>} style={{ height: "100%" }}>
              {pictureUrl ? (
                <div>
                  <a href={pictureUrl} target="_blank" rel="noopener noreferrer">
                    <img
                      src={pictureUrl}
                      alt="Last captured"
                      style={{ width: "100%", borderRadius: 4 }}
                    />
                  </a>
                  <div style={{ marginTop: 8 }}>
                    <Text type="secondary">
                      {lastPicture?.createdAt
                        ? `Captured ${relativeTime(lastPicture.createdAt)}`
                        : ""}
                    </Text>
                    {lastPicture?.qr ? (
                      <Tag color="green" style={{ marginLeft: 8 }}>
                        QR
                      </Tag>
                    ) : null}
                  </div>
                </div>
              ) : (
                <Empty description="No image captured" />
              )}
            </Card>
          </Col>
        </Row>

        {/* Alerts */}
        <Card title={<span>Alerts<InfoHint title="Health alerts raised for this device. Open ones can be turned into a repair ticket or resolved once fixed." /></span>} style={{ marginTop: 16 }}>
          <Table
            rowKey={(record) => record._id}
            dataSource={alerts}
            columns={alertColumns}
            pagination={false}
            locale={{ emptyText: "No alerts" }}
            size="small"
          />
        </Card>

        {/* Effective thresholds (read-only) */}
        <Card title={<span>Effective Thresholds<InfoHint title="The alert rules currently applied to this device — the limits that decide when it turns yellow or red. These come from the fleet defaults unless overridden for this device." /></span>} style={{ marginTop: 16 }}>
          <Descriptions bordered size="small" column={{ xs: 1, sm: 2, md: 3 }}>
            <Descriptions.Item label={<span>Offline After (mins)<InfoHint title="If no heartbeat is received for this many minutes, the device is flagged offline." /></span>}>
              {effectiveRule.offlineAfterMins ?? "—"}
            </Descriptions.Item>
            <Descriptions.Item label={<span>No Image After (mins)<InfoHint title="If no new photo is uploaded for this many minutes, a no-capture alert is raised." /></span>}>
              {effectiveRule.noImageAfterMins ?? "—"}
            </Descriptions.Item>
            <Descriptions.Item label={<span>Temp Above (°C)<InfoHint title="CPU temperature that triggers an over-temperature alert." /></span>}>
              {effectiveRule.tempAboveC ?? "—"}
            </Descriptions.Item>
            <Descriptions.Item label={<span>Storage Used Above (%)<InfoHint title="Disk-usage percentage that triggers a low-storage alert." /></span>}>
              {effectiveRule.storageUsedAbovePct ?? "—"}
            </Descriptions.Item>
            <Descriptions.Item label={<span>Weak Signal Below<InfoHint title="Network signal strength below this value triggers a weak-signal alert." /></span>}>
              {effectiveRule.weakSignalBelow ?? "—"}
            </Descriptions.Item>
            <Descriptions.Item label={<span>Under-voltage Detection<InfoHint title="When enabled, the device reports power under-voltage events (insufficient power supply)." /></span>}>
              {effectiveRule.underVoltageEnabled ? "Enabled" : "Disabled"}
            </Descriptions.Item>
            <Descriptions.Item label={<span>Reboot Detection<InfoHint title="When enabled, an alert is raised whenever the device unexpectedly reboots." /></span>}>
              {effectiveRule.rebootDetectedEnabled ? "Enabled" : "Disabled"}
            </Descriptions.Item>
          </Descriptions>
        </Card>
      </div>
    </Spin>
  );
};

export default DeviceHealth;
